import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const clearCache = () => {
  Object.keys(localStorage).filter((k) => k.startsWith("cache:")).forEach((k) => localStorage.removeItem(k));
};

// Server in avvio (502/503/504, rete assente o pagina HTML di attesa): un solo controllo condiviso
// su /health aspetta che il server sia pronto, poi ogni richiesta viene ripetuta una volta.
const WAKE_DELAYS = [1000, 2000, 3000, 5000, 8000, 10000, 15000];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
let waking = null;
export const isWaking = () => !!waking;
const ping = () => axios.get(`${API}/health`, { timeout: 8000 }).then((r) => r.data?.ok === true).catch(() => false);
export const waitForServer = () => {
  if (!waking) {
    waking = (async () => {
      for (const d of WAKE_DELAYS) {
        await sleep(d);
        if (await ping()) return true;
      }
      return false;
    })().finally(() => {
      waking = null;
      window.dispatchEvent(new CustomEvent("server-waking", { detail: false }));
    });
    window.dispatchEvent(new CustomEvent("server-waking", { detail: true }));
  }
  return waking;
};
// Sveglia il server in anticipo (es. mentre si compila il login).
export const warmUp = () => ping().then((ok) => ok || waitForServer());

const retry = async (cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  if (cfg._retried || (method !== "get" && !cfg.retryable)) return null;
  if (!(await waitForServer())) return null;
  cfg._retried = true;
  return api(cfg);
};
const isHtml = (res) => String(res.headers?.["content-type"] || "").includes("text/html");

// Azienda sospesa dal Super Admin: chiude la sessione e torna al login.
api.interceptors.response.use(
  async (res) => {
    if (isHtml(res)) {
      const again = await retry(res.config);
      if (again) return again;
      return Promise.reject(Object.assign(new Error("Network Error"), { code: "ERR_NETWORK", config: res.config }));
    }
    return res;
  },
  async (err) => {
    const status = err.response?.status;
    const transient = err.config && !axios.isCancel(err)
      && (err.response ? [502, 503, 504].includes(status) : navigator.onLine !== false);
    if (transient) {
      const again = await retry(err.config);
      if (again) return again;
    }
    const detail = err.response?.data?.detail;
    if (err.response?.status === 403 && typeof detail === "string" && detail.startsWith("Prova gratuita scaduta")) {
      window.dispatchEvent(new Event("trial-expired"));
    }
    if (err.response?.status === 403 && typeof detail === "string" && detail.startsWith("Account aziendale sospeso")
        && localStorage.getItem("token")) {
      localStorage.removeItem("token");
      clearCache();
      sessionStorage.setItem("login-notice", detail);
      if (window.location.pathname !== "/login") window.location.assign("/login");
    }
    return Promise.reject(err);
  }
);

// GET with offline fallback: caches successful responses in localStorage and
// serves the last cached copy when the network is unavailable.
export const cachedGet = async (path) => {
  const key = `cache:${path}`;
  try {
    const res = await api.get(path);
    try { localStorage.setItem(key, JSON.stringify(res.data)); } catch (_) {}
    return res;
  } catch (e) {
    const cached = localStorage.getItem(key);
    const isNetwork = !e.response || e.code === "ERR_NETWORK" || e.message === "Network Error";
    if (isNetwork && cached) {
      window.dispatchEvent(new CustomEvent("app-offline"));
      return { data: JSON.parse(cached), _cached: true };
    }
    throw e;
  }
};

export default api;
export { API };
